import { defineStore } from 'pinia';
import { ref } from 'vue';

export type MessageBoxType = 'confirm' | 'alert' | 'prompt';
export type MessageBoxVariant = 'info' | 'warning' | 'danger';

export interface MessageBoxOptions {
  type: MessageBoxType;
  /** Title shown in the modal header. Falls back to a localized default. */
  title?: string;
  /** Body text (supports multi-line via \n). */
  message: string;
  variant?: MessageBoxVariant;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Only used for prompt: pre-filled input value. */
  defaultValue?: string;
  inputPlaceholder?: string;
}

export interface ActiveMessageBox extends MessageBoxOptions {
  id: string;
}

/** Accepts either a plain message string or a full options object. */
export type MessageBoxInput = string | Omit<MessageBoxOptions, 'type'>;

interface QueuedMessageBox {
  options: MessageBoxOptions;
  resolve: (result: unknown) => void;
}

function normalizeInput(input: MessageBoxInput): Omit<MessageBoxOptions, 'type'> {
  return typeof input === 'string' ? { message: input } : input;
}

function nextId() {
  return `msgbox-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Promise-based message box store.
 * Mirrors the toast store pattern: components/composables push requests,
 * a single <MessageBoxHost /> mounted in App.vue renders the active box.
 */
export const useMessageBoxStore = defineStore('messageBox', () => {
  const active = ref<ActiveMessageBox | null>(null);
  const queue = ref<QueuedMessageBox[]>([]);
  let activeResolve: ((result: unknown) => void) | null = null;

  function open(options: MessageBoxOptions): Promise<unknown> {
    return new Promise((resolve) => {
      if (active.value) {
        // Serialize: only one modal at a time
        queue.value.push({ options, resolve });
        return;
      }
      active.value = { id: nextId(), ...options };
      activeResolve = resolve;
    });
  }

  /** Yes/No confirmation. Resolves `true` on confirm, `false` on cancel. */
  function confirm(input: MessageBoxInput): Promise<boolean> {
    return open({ type: 'confirm', ...normalizeInput(input) }) as Promise<boolean>;
  }

  /** Informational box with a single OK button. Resolves when dismissed. */
  function alert(input: MessageBoxInput): Promise<void> {
    return open({ type: 'alert', ...normalizeInput(input) }) as Promise<void>;
  }

  /** Text input box. Resolves the entered string, or `null` when cancelled. */
  function prompt(input: MessageBoxInput): Promise<string | null> {
    return open({ type: 'prompt', ...normalizeInput(input) }) as Promise<string | null>;
  }

  /** Dismiss the active box and resolve its promise, then pop the next queued box. */
  function resolveCurrent(result: unknown) {
    if (!active.value) return;
    const resolve = activeResolve;
    activeResolve = null;
    active.value = null;

    if (queue.value.length > 0) {
      const next = queue.value.shift()!;
      active.value = { id: nextId(), ...next.options };
      activeResolve = next.resolve;
    }

    resolve?.(result);
  }

  return {
    active,
    queue,
    confirm,
    alert,
    prompt,
    resolveCurrent,
  };
});
